import { request } from '@/api/http';
import type {
  Job,
  JobAttempt,
  JobAttemptListParams,
  JobItem,
  JobListParams,
  JobProgress,
  JobsDashboardResponse,
  JobSummary,
} from '@/api/types';

type RequestOpts = { signal?: AbortSignal };

function buildListQuery(params?: JobListParams): string {
  const search = new URLSearchParams();
  if (params?.type) search.set('type', params.type);
  if (params?.status) search.set('status', params.status);
  if (params?.search_id) search.set('search_id', params.search_id);
  if (params?.subscription_id) search.set('subscription_id', params.subscription_id);
  if (params?.created_after) search.set('created_after', params.created_after);
  if (params?.created_before) search.set('created_before', params.created_before);
  if (params?.workspace) search.set('workspace', params.workspace);
  if (params?.limit != null) search.set('limit', String(params.limit));
  if (params?.offset != null) search.set('offset', String(params.offset));
  return search.toString();
}

export const jobsService = {
  list: (params?: JobListParams, opts?: RequestOpts) => {
    const q = buildListQuery(params);
    return request<Job[]>(`/jobs${q ? `?${q}` : ''}`, opts);
  },
  dashboard: (params?: JobListParams, opts?: RequestOpts) => {
    const q = buildListQuery(params);
    return request<JobsDashboardResponse>(`/jobs/dashboard${q ? `?${q}` : ''}`, opts);
  },
  summary: (opts?: RequestOpts) => request<JobSummary>('/jobs/summary', opts),
  get: (jobId: string, opts?: RequestOpts) => request<Job>(`/jobs/${jobId}`, opts),
  getProgress: (jobId: string, opts?: RequestOpts) =>
    request<JobProgress>(`/jobs/${jobId}/progress`, opts),
  listItems: (
    jobId: string,
    params?: { status?: string; limit?: number; offset?: number },
    opts?: RequestOpts
  ) => {
    const search = new URLSearchParams();
    if (params?.status) search.set('status', params.status);
    if (params?.limit != null) search.set('limit', String(params.limit));
    if (params?.offset != null) search.set('offset', String(params.offset));
    const q = search.toString();
    return request<JobItem[]>(`/jobs/${jobId}/items${q ? `?${q}` : ''}`, opts);
  },
  listAttempts: (jobId: string, params?: JobAttemptListParams, opts?: RequestOpts) => {
    const search = new URLSearchParams();
    if (params?.status) search.set('status', params.status);
    if (params?.limit != null) search.set('limit', String(params.limit));
    if (params?.offset != null) search.set('offset', String(params.offset));
    const q = search.toString();
    return request<JobAttempt[]>(`/jobs/${jobId}/attempts${q ? `?${q}` : ''}`, opts);
  },
  cancel: (jobId: string, opts?: { force?: boolean }) =>
    request<{ id: string; status: string }>(`/jobs/${jobId}/cancel`, {
      method: 'POST',
      body: JSON.stringify({ force: opts?.force ?? false }),
    }),
  retry: (jobId: string) =>
    request<{ id: string; status: string; total_items: number; processed_items: number; failed_items: number }>(
      `/jobs/${jobId}/retry`,
      { method: 'POST' }
    ),
  remove: (jobId: string, opts?: { force?: boolean; alsoClearActivity?: boolean }) => {
    const params = new URLSearchParams();
    if (opts?.force) params.set('force', 'true');
    if (opts?.alsoClearActivity) params.set('also_clear_activity', 'true');
    const q = params.toString();
    return request<{ id: string; removed: boolean; status: string }>(`/jobs/${jobId}${q ? `?${q}` : ''}`, {
      method: 'DELETE',
    });
  },
  listActivity: (jobId: string, params?: { page?: number; limit?: number }) => {
    const search = new URLSearchParams();
    if (params?.page != null) search.set('page', String(params.page));
    if (params?.limit != null) search.set('limit', String(params.limit));
    const q = search.toString();
    return request<import('@/api/types').Paginated<import('@/api/types').ActivityLog>>(
      `/jobs/${jobId}/activity${q ? `?${q}` : ''}`
    );
  },
  bulkDelete: (ids: string[], opts?: { alsoClearActivity?: boolean; force?: boolean }) =>
    request<{ deleted: number; activity_deleted?: number; failed: number; errors: { id: string; error: string }[] }>(
      '/jobs/bulk/delete',
      {
        method: 'POST',
        body: JSON.stringify({
          ids,
          also_clear_activity: opts?.alsoClearActivity ?? false,
          force: opts?.force ?? false,
        }),
      },
    ),
  bulkCancel: (ids: string[], opts?: { force?: boolean }) =>
    request<{ cancelled: number; failed: number; errors: { id: string; error: string }[] }>(
      '/jobs/bulk/cancel',
      { method: 'POST', body: JSON.stringify({ ids, force: opts?.force ?? false }) },
    ),
  bulkRetry: (ids: string[]) =>
    request<{ retried: number; failed: number; errors: { id: string; error: string }[] }>(
      '/jobs/bulk/retry',
      { method: 'POST', body: JSON.stringify({ ids }) },
    ),
  cleanupPreview: (payload: {
    statuses?: string[];
    older_than_days?: number;
    job_ids?: string[];
    force?: boolean;
  }) =>
    request<{
      eligibleCount: number;
      matched?: number;
      statuses: string[];
      limit?: number;
      canClear?: boolean;
      reason?: string | null;
    }>('/jobs/cleanup/preview', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  cleanup: (payload: {
    statuses?: string[];
    older_than_days?: number;
    limit?: number;
    also_clear_activity?: boolean;
    job_ids?: string[];
    force?: boolean;
  }) =>
    request<{
      status?: string;
      deleted: number;
      deletedJobs?: number;
      deletedJobItems?: number;
      deletedRelatedRows?: number;
      activity_deleted?: number;
      matched?: number;
      failed: number;
      errors: { id: string; error: string }[];
    }>('/jobs/cleanup', { method: 'POST', body: JSON.stringify(payload) }),
  cancelItem: (jobId: string, itemId: string, opts?: { force?: boolean }) =>
    request<{ cancelled?: number; failed: number }>(`/jobs/${jobId}/items/${itemId}/cancel`, {
      method: 'POST',
      body: JSON.stringify({ force: opts?.force ?? false }),
    }),
  retryItem: (jobId: string, itemId: string) =>
    request<{ retried?: number; failed: number }>(`/jobs/${jobId}/items/${itemId}/retry`, {
      method: 'POST',
      body: JSON.stringify({}),
    }),
  bulkCancelItems: (itemIds: string[], opts?: { force?: boolean }) =>
    request<{ cancelled: number; failed: number; errors: { id: string; error: string }[] }>(
      '/jobs/bulk/items/cancel',
      { method: 'POST', body: JSON.stringify({ item_ids: itemIds, force: opts?.force ?? false }) },
    ),
  bulkRetryItems: (itemIds: string[]) =>
    request<{ retried: number; failed: number; errors: { id: string; error: string }[] }>(
      '/jobs/bulk/items/retry',
      { method: 'POST', body: JSON.stringify({ item_ids: itemIds }) },
    ),
  activityPreview: (jobId: string) =>
    request<{ search_executions: number; fetch_runs: number; deliveries: number; total: number }>(
      `/jobs/${jobId}/activity/preview`,
    ),
  clearActivity: (jobId: string) =>
    request<{ search_executions: number; fetch_runs: number; deliveries: number; total: number }>(
      `/jobs/${jobId}/activity/clear`,
      { method: 'POST' },
    ),
};
